const cities = [
  { name: "Melbourne", zips: "32901, 32934, 32935, 32940" },
  { name: "Palm Bay", zips: "32905, 32907, 32908, 32909" },
  { name: "Cocoa Beach", zips: "32931, 32932" },
  { name: "Satellite Beach", zips: "32937" },
  { name: "Indialantic", zips: "32903" },
  { name: "Viera", zips: "32940, 32955" },
  { name: "Rockledge", zips: "32955" },
  { name: "Titusville", zips: "32780, 32796" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or the beach in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function MelbourneLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Melbourne &amp; the Space Coast
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Melbourne, Palm Bay, Cocoa Beach, Satellite Beach, or anywhere along the Space Coast, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Melbourne sits at the center of Florida's Space Coast — a region defined by a unique blend of aerospace engineering, coastal living, and a pace of life that balances ambition with beachside relaxation. With a population approaching 85,000 within city limits and anchoring a broader Brevard County community of over 600,000, Melbourne is the economic and cultural hub of the central Atlantic coast. The city stretches from the Indian River Lagoon across the barrier island to the Atlantic Ocean, linked by the Melbourne Causeway — a drive that takes you from historic downtown Melbourne, through the waterfront communities of Indialantic and Melbourne Beach, straight to the sand and surf.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What makes Melbourne distinct among Florida coastal cities is the convergence of engineering talent, tourism, and space exploration. The presence of Northrop Grumman, L3Harris Technologies, and the nearby Kennedy Space Center and Cape Canaveral Space Force Station means Melbourne has one of the highest concentrations of aerospace engineers and defense contractors per capita in the United States. These professionals travel internationally for satellite launches, international partnerships, and technical conferences — often on compressed timelines dictated by mission schedules and launch windows rather than personal convenience. When a launch campaign in French Guiana or a payload review in Germany gets moved up by two weeks, an expired passport becomes an immediate crisis.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The residential landscape of Melbourne reflects its dual identity as a tech hub and beach town. Viera, master-planned and family-oriented, attracts engineers and young professionals with top-rated schools and proximity to both the coast and the major employers along the I-95 corridor. Satellite Beach and Indian Harbour Beach offer barrier island living for those who want the Atlantic in their backyard. Downtown Melbourne and historic Eau Gallie provide a walkable arts-and-dining scene with the Eau Gallie Arts District, independent galleries, and waterfront restaurants along the Indian River Lagoon. Palm Bay, Melbourne's larger neighbor to the south, offers more affordable housing stock and a growing community of families and commuters.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The travel patterns of Melbourne residents reflect the community's distinct demographics. Cruise travelers form a major segment — Port Canaveral, the busiest cruise port in the world, sits just 30 miles north of Melbourne and serves as the embarkation point for Disney Cruise Line, Royal Caribbean, Carnival, Norwegian, and MSC Caribbean itineraries. Melbourne families book Disney cruises for school breaks, retirees sail the Eastern Caribbean for winter getaways, and young couples take weekend Bahamas cruises. Every single cruise passenger needs a valid passport for most itineraries — and many discover expired or near-expired passports only when receiving their boarding documents, creating a rush that standard processing cannot accommodate.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Beyond cruising, Melbourne's beach lifestyle creates its own travel patterns. Surfers from Satellite Beach and Cocoa Beach travel to Costa Rica, Nicaragua, and Puerto Rico for swell seasons. Beachside families vacation in Mexico and the Dominican Republic during spring break. And the growing number of remote tech workers — software engineers and project managers who followed the post-pandemic migration to Florida's coast — travel internationally for company offsites, client meetings, and extended workations in Europe, Asia, and Latin America.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            The geographic reality of Melbourne matters for passport processing. The closest regional passport agency is in Miami, approximately 170 miles south via the Florida Turnpike and I-95 — a drive that consumes three hours in typical traffic and longer during tourist season. For a Melbourne aerospace engineer who discovers an expired passport the week before a launch campaign in French Guiana, or a Viera family that realizes their child's passport expired months ago while booking a Disney cruise, that drive is not just inconvenient — it may be impossible. Fast Passport Center has processed expedited passports for Melbourne travelers across every demographic: engineers, cruise passengers, surfers, retirees, and beachside families. Our courier-based expedited service means you start online from your Viera home office, your Satellite Beach condo, or your downtown Melbourne apartment — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Melbourne — approximately 140 miles south via I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Brevard County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Brevard County — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Cocoa Beach", href: "/cocoa-beach" },
                  { name: "Titusville", href: "/titusville" },
                  { name: "Orlando", href: "/orlando" },
                  { name: "Daytona Beach", href: "/daytona-beach" },
                  { name: "Jupiter", href: "/jupiter" },
                  { name: "Stuart", href: "/stuart" },
                  { name: "Port St. Lucie", href: "/port-st-lucie" },
                  { name: "West Palm Beach", href: "/west-palm-beach" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}